import { useParams, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect, useMemo } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import PROFESSION_DATA from '../data/professionData';
import StickyScanBar from '../components/landing/StickyScanBar';
import { useVisitorCity } from '../hooks/useVisitorCity';
import LandingNav from '../components/landing/LandingNav';
import LandingFooter from '../components/landing/LandingFooter';
import SubSpecialtySelector from '../components/landing/SubSpecialtySelector';
import { getArticle, getArticleUpper } from '@/utils/grammar';

/* ── Scroll Reveal ── */
function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }} className={className}>
      {children}
    </motion.div>
  );
}

/* ── Animated Counter ── */
function AnimatedCounter({ target, suffix = '', decimals = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isInView) return;
    const dur = 1500, start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setCount(decimals > 0 ? parseFloat((e * target).toFixed(decimals)) : Math.round(e * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [isInView, target, decimals]);
  return <span ref={ref}>{count}{suffix}</span>;
}

/* ── Animated Stat (for The Shift section) ── */
function AnimatedStat({ target, decimals = 0, suffix = '', color, label, delay: delayMs }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (!isInView || started) return;
    const timer = setTimeout(() => {
      setStarted(true);
      if (target === 0) { setCount(0); return; }
      const dur = 1200, start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / dur, 1);
        const e = 1 - Math.pow(1 - p, 3);
        setCount(decimals > 0 ? parseFloat((e * target).toFixed(decimals)) : Math.round(e * target));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [isInView, started, target, decimals, delayMs]);
  return (
    <div ref={ref} className="mb-8">
      <p style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, color, fontSize: '48px', lineHeight: 1 }}>{count}{suffix}</p>
      <p className="mt-1" style={{ fontSize: '12px', color: 'rgba(240,235,227,0.5)', lineHeight: 1.5 }}>{label}</p>
    </div>
  );
}

/* ── ChatGPT Typing Cursor ── */
function TypingCursor() {
  return <span className="inline-block w-[2px] h-[13px] ml-0.5 animate-pulse" style={{ backgroundColor: 'rgba(240,235,227,0.6)', verticalAlign: 'text-bottom' }} />;
}

/* ══════════════════════════════════════════
   PROFESSION LANDING — Full Transformation Journey
   ══════════════════════════════════════════ */
export default function ProfessionLanding() {
  const { slug, sub } = useParams();
  const navigate = useNavigate();
  const [scanInput, setScanInput] = useState('');
  const visitorCity = useVisitorCity('your city');

  // Scroll to top on mount / slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const baseProfession = PROFESSION_DATA[slug];

  // Merge sub-specialty data if active
  const activeSub = sub && baseProfession?.isGroup
    ? baseProfession.subSpecialties?.find(s => s.slug === sub)
    : null;

  const prof = useMemo(() => {
    if (!baseProfession) return null;
    if (!activeSub) return baseProfession;
    // Merge sub-specialty overrides onto base profession
    return {
      ...baseProfession,
      chatQuery: activeSub.chatQuery || baseProfession.chatQuery,
      competitors: activeSub.competitors || baseProfession.competitors,
      artifact1Title: activeSub.artifact1Title || baseProfession.artifact1Title,
      artifact1Preview: activeSub.artifact1Preview || baseProfession.artifact1Preview,
      smsPreview: activeSub.smsPreview || baseProfession.smsPreview,
      scanPlaceholder: activeSub.scanPlaceholder || baseProfession.scanPlaceholder,
      gaps: activeSub.gaps || baseProfession.gaps,
      // Display name updates for sub-specialty context
      _subName: activeSub.name,
      _subCustomerType: activeSub.customerType,
    };
  }, [baseProfession, activeSub]);

  if (!prof) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#EDE8DF', fontFamily: "'DM Sans', sans-serif" }}>
        <div className="text-center">
          <p style={{ color: '#3C3226', fontSize: '18px' }}>Profession not found.</p>
          <button onClick={() => navigate('/')} className="mt-4 underline" style={{ color: '#B8985A' }}>Back to home</button>
        </div>
      </div>
    );
  }

  const handleScan = () => {
    if (scanInput.trim()) navigate('/onboarding', { state: { businessName: scanInput.trim() } });
    else navigate('/onboarding');
  };

  const siteUrl = window.location.origin;
  const pageUrl = activeSub ? `${siteUrl}/for/${slug}/${activeSub.slug}` : `${siteUrl}/for/${slug}`;
  const ogImage = `${siteUrl}/images/og/${slug}.png`;
  const seoName = activeSub ? activeSub.name : prof.name;
  const seoPlural = activeSub ? `${activeSub.name}s` : prof.plural;
  const seoCustomer = activeSub ? activeSub.customerType : prof.customerType;
  const metaTitle = `GetChosenFirst for ${seoPlural} | AI Recommendation Monitoring`;
  const metaDesc = `When ${seoCustomer} in ${visitorCity} asks AI for ${getArticle(seoName)} ${seoName.toLowerCase()}, your name should be part of the answer. See where you stand across 9 AI engines.`;

  return (
    <div className="min-h-screen" style={{ fontFamily: "'DM Sans', sans-serif" }} data-testid="profession-landing-page">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      {/* Navbar */}
      <LandingNav />

      {/* ════════ SECTION 1: PROFESSION HERO ════════ */}
      <ProfessionHero prof={prof} scanInput={scanInput} setScanInput={setScanInput} handleScan={handleScan} visitorCity={visitorCity} activeSub={activeSub} />

      {/* ════════ SUB-SPECIALTY SELECTOR (Group Professions Only) ════════ */}
      {baseProfession.isGroup && (
        <SubSpecialtySelector profession={baseProfession} activeSub={sub} basePath={`/for/${slug}`} />
      )}

      {/* ════════ ACT 2 — DISRUPTION ════════ */}
      <div style={{ backgroundColor: '#1A1A2E' }}>
        {/* SECTION 2: THE SHIFT */}
        <section className="py-[5.25rem] md:py-24 px-6 md:px-10" data-testid="profession-shift">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-12 md:gap-16">
            <div className="md:w-[60%]">
              <p className="mb-6" style={{ fontSize: '11px', color: '#B8985A', textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 500 }}>THE SHIFT IS ALREADY HAPPENING</p>
              <h2 className="mb-8" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 3.5vw, 36px)', fontWeight: 300, color: '#F0EBE3', lineHeight: 1.3 }}>
                While you were {prof.workVerb}, AI started choosing which {prof.name.toLowerCase()} gets mentioned in your city.
              </h2>
              <p style={{ fontSize: '16px', fontWeight: 300, color: 'rgba(240,235,227,0.6)', maxWidth: '480px', lineHeight: 1.8 }}>
                Right now, when a {prof.customerType} in your city asks ChatGPT, Gemini, or Perplexity for {getArticle(prof.name)} {prof.name.toLowerCase()} recommendation, AI picks the answer. If your {prof.businessWord} isn't optimized for how AI thinks, you're invisible.
              </p>
            </div>
            <div className="md:w-[40%] flex flex-col justify-center">
              <AnimatedStat target={73} suffix="%" color="#B8985A" label="of consumers now use AI to find local services" delay={0} />
              <AnimatedStat target={4.2} decimals={1} suffix="B" color="#B8985A" label="AI search queries per month in 2026" delay={400} />
              <AnimatedStat target={0} suffix="%" color="#C67A5C" label="of your competitors are optimized for it" delay={800} />
            </div>
          </div>
        </section>

        {/* SECTION 3: THE PROOF */}
        <section className="py-[5.25rem] px-6" data-testid="profession-proof">
          <div className="max-w-3xl mx-auto">
            <Reveal>
              <p className="text-center mb-12" style={{ fontSize: '11px', color: '#B8985A', textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 500 }}>
                THIS IS WHAT AI SAYS WHEN YOUR {prof.customerType.toUpperCase()} ASKS
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mx-auto max-w-[520px] p-6 md:p-8" style={{ backgroundColor: 'rgba(240,235,227,0.04)', border: '1px solid rgba(240,235,227,0.08)', borderRadius: '4px' }}>
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(16,163,127,0.15)' }}>
                    <span style={{ fontSize: '10px', color: '#10A37F' }}>G</span>
                  </div>
                  <span style={{ fontSize: '12px', color: 'rgba(240,235,227,0.5)' }}>ChatGPT</span>
                </div>
                <p className="mb-4" style={{ fontSize: '14px', color: 'rgba(240,235,227,0.4)', fontStyle: 'italic' }}>"{prof.chatQuery}"</p>
                <div className="space-y-4 mb-6">
                  {prof.competitors.map((c, i) => (
                    <div key={i} className="flex gap-3">
                      <span style={{ fontSize: '14px', color: '#B8985A', fontWeight: 600, flexShrink: 0 }}>{i + 1}.</span>
                      <div>
                        <p style={{ fontSize: '14px', color: '#F0EBE3', fontWeight: 500 }}>{c.name}</p>
                        <p style={{ fontSize: '12px', color: 'rgba(240,235,227,0.5)', lineHeight: 1.5 }}>{c.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-4" style={{ borderTop: '1px solid rgba(240,235,227,0.08)' }}>
                  <p style={{ fontSize: '14px', color: '#C67A5C', fontWeight: 500 }}>Your {prof.businessWord} isn't here.<TypingCursor /></p>
                  <p className="mt-2" style={{ fontSize: '12px', color: 'rgba(240,235,227,0.4)' }}>We're changing that.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* SECTION 4: THE ENGINE */}
        <section className="py-[5.25rem] px-6" data-testid="profession-engine">
          <div className="max-w-3xl mx-auto text-center">
            <Reveal>
              <p className="mb-6" style={{ fontSize: '11px', color: '#B8985A', textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 500 }}>OUR AI MONITORING ENGINE</p>
              <h2 className="mb-12" style={{ fontFamily: "'Playfair Display', serif", fontSize: '28px', fontWeight: 300, color: '#F0EBE3' }}>9 AI engines. Scanned simultaneously.</h2>
            </Reveal>
            <EngineGrid />
            <Reveal delay={0.3}>
              <p className="mt-10" style={{ fontSize: '14px', color: 'rgba(240,235,227,0.5)' }}>
                Now let us show you what we build for {prof.plural.toLowerCase()}.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Gradient back to warm */}
        <div style={{ height: '200px', background: 'linear-gradient(to bottom, #1A1A2E, #EDE8DF)' }} />
      </div>

      {/* ════════ ACT 3 — RESOLUTION ════════ */}

      {/* SECTION 5: WHAT WE BUILD */}
      <section className="py-[5.25rem] px-6" style={{ backgroundColor: '#EDE8DF' }} data-testid="profession-build">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.2em', color: '#3C3226', fontSize: '28px' }}>
                WHAT WE CREATE FOR {prof.plural.toUpperCase()}
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-5 max-w-[700px] mx-auto">
            {/* Card 1: AI-Optimized Content */}
            <Reveal>
              <div className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', borderRadius: '2px' }} data-testid="artifact-content">
                <div className="mb-4 p-4" style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8', borderRadius: '2px' }}>
                  <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '14px', color: '#3C3226', lineHeight: 1.4 }}>{prof.artifact1Title}</p>
                  <p className="mt-2" style={{ fontSize: '11px', color: '#5C5347', lineHeight: 1.5 }}>{prof.artifact1Preview}</p>
                  <span className="inline-block mt-3 px-2 py-1 text-[10px] font-medium" style={{ backgroundColor: 'rgba(29,107,79,0.08)', color: '#1D6B4F' }}>FAQ Schema included</span>
                </div>
                <p style={{ fontSize: '11px', color: '#8C7E6E', lineHeight: 1.5 }}>We generate AI-optimized content structured for mention by ChatGPT, Gemini, and Claude.</p>
              </div>
            </Reveal>

            {/* Card 2: Review Coaching */}
            <Reveal delay={0.1}>
              <div className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', borderRadius: '2px' }} data-testid="artifact-reviews">
                <div className="mb-4">
                  <div className="space-y-2">
                    {['How was your experience?', 'What stood out most?', 'Post to Google'].map((step, i) => (
                      <div key={step} className="flex items-center gap-2.5">
                        <div className="flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: i < 2 ? '#1D6B4F' : 'rgba(29,107,79,0.15)', color: i < 2 ? '#fff' : '#1D6B4F', fontSize: '9px', fontWeight: 600 }}>{i + 1}</div>
                        <p style={{ fontSize: '12px', color: i < 2 ? '#3C3226' : '#8C7E6E', lineHeight: 1.4 }}>{step}</p>
                        {i < 2 && <CheckCircle2 size={11} color="#1D6B4F" className="ml-auto flex-shrink-0" />}
                      </div>
                    ))}
                  </div>
                </div>
                <p style={{ fontSize: '11px', color: '#8C7E6E', lineHeight: 1.5 }}>Personalized review requests you can send to recent customers — with guided coaching that helps them write reviews AI engines trust.</p>
              </div>
            </Reveal>

            {/* Card 3: Directory Guidance */}
            <Reveal delay={0.2}>
              <div className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', borderRadius: '2px' }} data-testid="artifact-directory">
                <div className="mb-4 space-y-2.5">
                  {[{ dir: 'Google Business', status: 'Claimed', done: true }, { dir: 'Yelp', status: 'Not yet', done: false }, { dir: 'Apple Maps', status: 'Not yet', done: false }].map(d => (
                    <div key={d.dir} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {d.done ? <CheckCircle2 size={12} color="#1D6B4F" /> : <div className="w-3 h-3 rounded-full border" style={{ borderColor: '#DDD5C8' }} />}
                        <p style={{ fontSize: '12px', color: d.done ? '#3C3226' : '#8C7E6E' }}>{d.dir}</p>
                      </div>
                      {!d.done && <span style={{ fontSize: '10px', color: '#B8985A', fontWeight: 500 }}>Claim now &rarr;</span>}
                      {d.done && <span style={{ fontSize: '10px', color: '#1D6B4F', fontWeight: 500 }}>Verified</span>}
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: '11px', color: '#8C7E6E', lineHeight: 1.5 }}>We show you exactly which directories matter, prepare your information, and guide you through claiming your listings on 15+ platforms AI engines trust.</p>
              </div>
            </Reveal>

            {/* Card 4: Gap Analysis */}
            <Reveal delay={0.3}>
              <div className="p-6" style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', borderRadius: '2px' }} data-testid="artifact-gaps">
                <div className="mb-4 space-y-2.5">
                  {prof.gaps.map(gap => (
                    <div key={gap.text} className="flex items-start gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: gap.color }} />
                      <p style={{ fontSize: '12px', color: '#3C3226', lineHeight: 1.4 }}>{gap.text} <span className="font-semibold" style={{ color: '#1D6B4F' }}>&#8212; +{gap.pts} pts</span></p>
                    </div>
                  ))}
                  <p className="text-right font-semibold mt-2" style={{ fontSize: '13px', color: '#1D6B4F' }}>+{prof.gaps.reduce((s, g) => s + g.pts, 0)} pts possible</p>
                </div>
                <p style={{ fontSize: '11px', color: '#8C7E6E', lineHeight: 1.5 }}>We identify every gap between you and the {prof.plural.toLowerCase()} AI currently mentions — and show you exactly how to close them.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 6: THE NUMBERS */}
      <section className="flex items-center justify-center px-6 py-[5.25rem]" style={{ backgroundColor: '#EDE8DF' }} data-testid="profession-numbers">
        <div className="max-w-4xl mx-auto w-full">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 text-center">
            {[{ value: 9, label: 'AI ENGINES MONITORED' }, { value: 14, label: 'QUERIES PER SCAN' }, { value: 0, label: 'CONTINUOUS MONITORING', display: 'Weekly' }, { value: 3, label: `${(activeSub?.name || prof.name).toUpperCase()} SEATS PER CITY` }].map((s, i) => (
              <Reveal key={s.label} delay={i * 0.12}>
                <div>
                  <p style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, color: '#3C3226', fontSize: 'clamp(44px, 6vw, 80px)', lineHeight: 1 }}>{s.display || <AnimatedCounter target={s.value} />}</p>
                  <p className="mt-3" style={{ color: '#5C5347', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em' }}>{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6.5: MONITORING STATEMENT */}
      <section className="py-[4.4rem] px-6 flex items-center justify-center" style={{ backgroundColor: '#EDE8DF' }} data-testid="profession-monitoring">
        <Reveal>
          <div className="text-center max-w-lg mx-auto">
            <p style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, color: '#3C3226', fontSize: 'clamp(22px, 3vw, 28px)', lineHeight: 1.5, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Your AI presence isn't a project. It's a practice.
            </p>
            <p className="mt-6" style={{ color: '#5C5347', fontSize: '15px', lineHeight: 1.8 }}>
              The scan shows you where you stand. The platform keeps you there.
            </p>
          </div>
        </Reveal>
      </section>

      {/* SECTION 7: TESTIMONIAL */}
      <section className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '60vh' }} data-testid="profession-testimonial">
        {/* Full-bleed background image */}
        <img
          src={prof.testimonialPhoto}
          alt={`${prof.name} client experience`}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.4)' }}
        />
        <Reveal>
          <div className="relative z-10 max-w-2xl mx-auto text-center px-6 py-20">
            <p style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 400, color: '#F0EBE3', fontSize: 'clamp(20px, 3vw, 28px)', lineHeight: 1.5 }} className="mb-8">
              "{prof.testimonial.quote}"
            </p>
            <p style={{ color: 'rgba(240,235,227,0.8)', fontSize: '13px' }}>
              <span className="font-medium" style={{ color: '#F0EBE3' }}>{prof.testimonial.name}</span>
              <span className="mx-2" style={{ color: 'rgba(240,235,227,0.4)' }}>&mdash;</span>
              <span style={{ color: 'rgba(240,235,227,0.6)' }}>{prof.testimonial.business}, {prof.testimonial.city}</span>
            </p>
          </div>
        </Reveal>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-6" style={{ backgroundColor: '#2C2520' }} data-testid="profession-final-cta">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="mb-4" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.15em', color: '#F0EBE3', fontSize: '24px' }}>
            THE FUTURE IS ALREADY CHOOSING.
          </h2>
          <p className="mb-8" style={{ color: 'rgba(240,235,227,0.6)', fontSize: '14px' }}>
            AI is mentioning your competitors right now. Find out where you stand.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch gap-0 max-w-lg mx-auto">
            <input type="text" value={scanInput} onChange={e => setScanInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScan()} placeholder={prof.scanPlaceholder} className="flex-1 px-5 py-4 text-[15px] outline-none" style={{ backgroundColor: 'rgba(247,244,239,0.08)', border: '1px solid rgba(240,235,227,0.12)', borderRight: 'none', borderRadius: 0, color: '#F0EBE3' }} data-testid="profession-final-scan-input" />
            <button onClick={handleScan} className="px-8 py-4 text-[13px] font-medium uppercase tracking-widest whitespace-nowrap transition-opacity hover:opacity-90" style={{ backgroundColor: '#B8985A', color: '#F0EBE3', borderRadius: 0 }} data-testid="profession-final-cta-btn">
              Scan now <ArrowRight size={14} className="inline ml-1" />
            </button>
          </div>
          <p className="mt-8" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', color: 'rgba(240,235,227,0.4)', fontSize: '14px' }}>Giving the power back to you.</p>
        </div>
      </section>

      {/* FOOTER */}
      <LandingFooter />

      <StickyScanBar />
    </div>
  );
}

/* ══════════════════════════════════════════
   HERO WITH PARALLAX
   ══════════════════════════════════════════ */
function ProfessionHero({ prof, scanInput, setScanInput, handleScan, visitorCity, activeSub }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const heroName = activeSub ? activeSub.name : prof.name;
  const heroCustomer = activeSub ? activeSub.customerType : prof.customerType;
  const heroArticle = getArticleUpper(heroName);

  return (
    <section ref={ref} className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '100vh' }} data-testid="profession-hero">
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img src={prof.photo} alt={prof.name} className="w-full h-full object-cover" style={{ filter: 'brightness(0.4) saturate(0.85)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(44,37,32,0.4) 0%, rgba(44,37,32,0.15) 50%, rgba(44,37,32,0.5) 100%)' }} />
      </motion.div>
      <motion.div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ opacity: contentOpacity }}>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} style={{ fontFamily: "'Playfair Display', serif", fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.2em', color: '#F0EBE3', fontSize: 'clamp(28px, 5vw, 56px)', lineHeight: 1.15 }} data-testid="profession-hero-headline">
          GET CHOSEN FIRST AS {heroArticle} {heroName.toUpperCase()}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} className="mt-6 mx-auto" style={{ color: 'rgba(240,235,227,0.7)', fontSize: '16px', maxWidth: '500px', lineHeight: 1.7 }}>
          When {heroCustomer} asks AI for {/^[aeiou]/i.test(heroName) ? 'an' : 'a'} {heroName.toLowerCase()} in {visitorCity}, your name should be part of the answer.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.7 }} className="flex flex-col sm:flex-row items-stretch gap-0 max-w-xl mx-auto mt-8">
          <input type="text" value={scanInput} onChange={e => setScanInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScan()} placeholder={prof.scanPlaceholder} className="flex-1 px-5 py-4 text-[15px] outline-none" style={{ backgroundColor: 'rgba(247,244,239,0.95)', border: '1px solid rgba(212,201,184,0.5)', borderRight: 'none', borderRadius: 0, color: '#3C3226' }} data-testid="profession-scan-input" />
          <button onClick={handleScan} className="px-8 py-4 text-[13px] font-medium uppercase tracking-widest whitespace-nowrap transition-opacity hover:opacity-90" style={{ backgroundColor: '#2C2520', color: '#F0EBE3', borderRadius: 0 }} data-testid="profession-cta-btn">Scan now <ArrowRight size={14} className="inline ml-1" /></button>
        </motion.div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-4" style={{ color: '#8C7E6E', fontSize: '12px' }}>Free. 60 seconds. No credit card.</motion.p>
      </motion.div>

      {/* Glowing scroll indicator — pinned to bottom of hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center cursor-pointer"
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
        data-testid="scroll-down-arrow"
      >
        <span className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: '#F0EBE3' }}>Scroll to explore</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="relative"
        >
          <div className="absolute rounded-full blur-md" style={{ backgroundColor: 'rgba(240,235,227,0.3)', width: 36, height: 36, top: -2, left: -2 }} />
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="relative z-10" style={{ filter: 'drop-shadow(0 0 10px rgba(240,235,227,0.5))' }}>
            <circle cx="16" cy="16" r="15" stroke="rgba(240,235,227,0.5)" strokeWidth="1" />
            <path d="M11 14L16 19L21 14" stroke="#F0EBE3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ══════════════════════════════════════════
   ENGINE GRID — 3x3 with sequential activation
   ══════════════════════════════════════════ */
function EngineGrid() {
  const engines = ['ChatGPT', 'Claude', 'Gemini', 'Perplexity', 'Copilot', 'Grok', 'Meta AI', 'You.com', 'Brave AI'];
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activated, setActivated] = useState([]);

  useEffect(() => {
    if (!isInView) return;
    engines.forEach((_, i) => {
      setTimeout(() => setActivated(prev => [...prev, i]), 200 * (i + 1));
    });
  }, [isInView]);

  return (
    <div ref={ref} className="grid grid-cols-3 gap-3 max-w-[340px] mx-auto">
      {engines.map((engine, i) => {
        const isActive = activated.includes(i);
        return (
          <motion.div
            key={engine}
            className="flex items-center justify-center py-3 px-2 text-center transition-all duration-500"
            style={{
              backgroundColor: isActive ? 'rgba(184,152,90,0.08)' : 'rgba(240,235,227,0.03)',
              border: `1px solid ${isActive ? 'rgba(184,152,90,0.3)' : 'rgba(240,235,227,0.06)'}`,
              borderRadius: '2px',
            }}
            animate={isActive ? { scale: [1, 1.03, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            <span style={{ fontSize: '11px', color: isActive ? '#B8985A' : 'rgba(240,235,227,0.25)', fontWeight: isActive ? 500 : 400, letterSpacing: '0.02em' }}>{engine}</span>
          </motion.div>
        );
      })}
    </div>
  );
}
